/**
 * Comprehensive Behavior Tracking Service
 *
 * Tracks user interactions including:
 * - Page views
 * - Button clicks
 * - Form submissions
 * - Feature usage
 */

import { supabase } from '../lib/supabaseClient';
import { getAppMode } from '../utils/appMode';

// Generate a session ID that persists for the browser session
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('vt_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('vt_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Get browser name
const getBrowserName = (): string => {
  const userAgent = navigator.userAgent;
  const appMode = getAppMode();

  // If in app mode, append that to the browser name
  if (appMode === 'app') {
    if (userAgent.includes('Chrome')) return 'Chrome (Standalone)';
    if (userAgent.includes('Safari')) return 'Safari (Standalone)';
    if (userAgent.includes('Firefox')) return 'Firefox (Standalone)';
    if (userAgent.includes('Edge')) return 'Edge (Standalone)';
    return 'App Mode';
  }

  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Other';
};

interface TrackEventOptions {
  eventData?: Record<string, any>;
  referrerPath?: string;
}

/**
 * Track a page view
 */
export const trackPageView = async (
  pageName: string,
  pagePath: string,
  options: TrackEventOptions = {}
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('user_behavior_events').insert({
      user_id: user?.id || null,
      session_id: getSessionId(),
      event_type: 'page_view',
      event_name: pageName,
      event_category: 'navigation',
      event_data: {
        ...options.eventData,
        app_mode: getAppMode()
      },
      page_path: pagePath,
      referrer_path: options.referrerPath || document.referrer,
      device_type: getDeviceType(),
      browser: getBrowserName()
    });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

/**
 * Track a button click
 */
export const trackButtonClick = async (
  buttonName: string,
  category: string = 'engagement',
  options: TrackEventOptions = {}
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('user_behavior_events').insert({
      user_id: user?.id || null,
      session_id: getSessionId(),
      event_type: 'button_click',
      event_name: buttonName,
      event_category: category,
      event_data: options.eventData || null,
      page_path: window.location.pathname,
      referrer_path: options.referrerPath,
      device_type: getDeviceType(),
      browser: getBrowserName()
    });
  } catch (error) {
    console.error('Error tracking button click:', error);
  }
};

/**
 * Track a form submission
 */
export const trackFormSubmit = async (
  formName: string,
  success: boolean,
  options: TrackEventOptions = {}
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('user_behavior_events').insert({
      user_id: user?.id || null,
      session_id: getSessionId(),
      event_type: 'form_submit',
      event_name: formName,
      event_category: success ? 'conversion' : 'engagement',
      event_data: { ...options.eventData, success },
      page_path: window.location.pathname,
      referrer_path: options.referrerPath,
      device_type: getDeviceType(),
      browser: getBrowserName()
    });
  } catch (error) {
    console.error('Error tracking form submit:', error);
  }
};

/**
 * Track feature usage
 */
export const trackFeatureUse = async (
  featureName: string,
  category: string = 'feature',
  options: TrackEventOptions = {}
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('user_behavior_events').insert({
      user_id: user?.id || null,
      session_id: getSessionId(),
      event_type: 'feature_use',
      event_name: featureName,
      event_category: category,
      event_data: options.eventData || null,
      page_path: window.location.pathname,
      referrer_path: options.referrerPath,
      device_type: getDeviceType(),
      browser: getBrowserName()
    });
  } catch (error) {
    console.error('Error tracking feature use:', error);
  }
};

/**
 * Track an error
 */
export const trackError = async (
  errorName: string,
  errorDetails: Record<string, any>,
  options: TrackEventOptions = {}
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('user_behavior_events').insert({
      user_id: user?.id || null,
      session_id: getSessionId(),
      event_type: 'error',
      event_name: errorName,
      event_category: 'error',
      event_data: errorDetails,
      page_path: window.location.pathname,
      referrer_path: options.referrerPath,
      device_type: getDeviceType(),
      browser: getBrowserName()
    });
  } catch (error) {
    console.error('Error tracking error:', error);
  }
};

/**
 * Track Vybe card opened (user clicked on a Vybe card to view details)
 */
export const trackVybeCardOpened = async (
  vybeId: string,
  vybeTitle: string,
  category: string,
  source: 'feed' | 'search' | 'featured' | 'recommended' | 'my_vybes' | 'favorites' = 'feed'
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('user_behavior_events').insert({
      user_id: user?.id || null,
      session_id: getSessionId(),
      event_type: 'vybe_interaction',
      event_name: 'vybe_card_opened',
      event_category: 'engagement',
      event_data: { vybe_id: vybeId, vybe_title: vybeTitle, category, source },
      page_path: window.location.pathname,
      device_type: getDeviceType(),
      browser: getBrowserName()
    });
  } catch (error) {
    console.error('Error tracking vybe card opened:', error);
  }
};

/**
 * Track Join Vybe button clicked
 */
export const trackJoinVybeClicked = async (
  vybeId: string,
  vybeTitle: string,
  source: 'card_cta' | 'detail_page' | 'sticky_bar' | 'header_button',
  isBotVybe: boolean = false
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('user_behavior_events').insert({
      user_id: user?.id || null,
      session_id: getSessionId(),
      event_type: 'vybe_interaction',
      event_name: 'join_vybe_clicked',
      event_category: 'engagement',
      event_data: { vybe_id: vybeId, vybe_title: vybeTitle, source, is_bot_vybe: isBotVybe },
      page_path: window.location.pathname,
      device_type: getDeviceType(),
      browser: getBrowserName()
    });
  } catch (error) {
    console.error('Error tracking join vybe clicked:', error);
  }
};

/**
 * Track Join Vybe request result (success or blocked)
 */
export const trackJoinVybeResult = async (
  vybeId: string,
  vybeTitle: string,
  result: 'success' | 'blocked_no_tokens' | 'blocked_limit' | 'blocked_already_applied' | 'blocked_error',
  errorMessage?: string
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('user_behavior_events').insert({
      user_id: user?.id || null,
      session_id: getSessionId(),
      event_type: 'vybe_interaction',
      event_name: `join_vybe_${result}`,
      event_category: result === 'success' ? 'conversion' : 'blocked',
      event_data: {
        vybe_id: vybeId,
        vybe_title: vybeTitle,
        result,
        error_message: errorMessage
      },
      page_path: window.location.pathname,
      device_type: getDeviceType(),
      browser: getBrowserName()
    });
  } catch (error) {
    console.error('Error tracking join vybe result:', error);
  }
};

/**
 * Get behavior summary for a user (admin only)
 */
export const getUserBehaviorSummary = async (
  userId: string,
  daysBack: number = 30
): Promise<any> => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_behavior_summary', {
        target_user_id: userId,
        days_back: daysBack
      });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting behavior summary:', error);
    return null;
  }
};

/**
 * Get recent events for a user (admin only)
 */
export const getUserRecentEvents = async (
  userId: string,
  limit: number = 50
): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('user_behavior_events')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting recent events:', error);
    return [];
  }
};
